/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import {
	Pressable,
	View,
	Text,
	ErrorText,
	Divider,
	TextField,
	Image,
	Button,
	Switch,
	Logo,
} from '@components/atoms'
import { ForgotPasswordMessage } from '@components/molecules'
import { SimpleNav } from '@components/systems'
import colorLogo from '@public/images/logo/logo.png'
import loginBg from '@public/images/background/login_bg.jpg'

import packageJson from '../../../../package.json'
import { grey } from '@mui/material/colors'
import { produce } from 'immer'
import {
	useAppSelector,
	useAppDispatch,
	authActions,
	configActions,
	AuthStateType,
} from '@redux/index'
import { useNavigate } from 'react-router-dom'

const LoginPage = () => {
	// redux dispatch
	const reduxDispatch = useAppDispatch()

	// hook
	const navigate = useNavigate()

	// variables
	const localUserID = localStorage.getItem('userAccount') ?? ''
	const defaultRememberIdYn: boolean = React.useMemo(() => {
		if (localUserID === '') {
			return false
		} else {
			return true
		}
	}, [])
	const defualtAccount: AuthStateType['login'] = defaultRememberIdYn
		? { email: localUserID, password: '' }
		: { email: '', password: '' }

	// redux state
	const systemName: string = useAppSelector(state => state.config.systemName)
	const systemSubtitle: string = useAppSelector(state => state.config.systemSubtitle)
	const isLoading: boolean = useAppSelector(state => state.config.isLoading as boolean)
	const isLogin: boolean = useAppSelector(state => state.auth.isLogin as boolean)
	const loginErrorMessage: string = useAppSelector(state => state.auth.loginErrorMessage)
	const isMobile: boolean = useAppSelector(state => state.config.isMobile)
	const isRememberId: boolean = useAppSelector(state => state.auth.isRememberId)

	// state
	const [account, setAccount] = React.useState(defualtAccount)
	const [showForgotPassword, setShowForgotPassword] = React.useState<boolean>(false)

	React.useEffect(() => {
		reduxDispatch(authActions.IS_REMEMBER_ID(defaultRememberIdYn))
	}, [defaultRememberIdYn])

	React.useEffect(() => {
		if (isLoading) {
			const timeout = setTimeout(() => {
				reduxDispatch(configActions.IS_LOADING(false))
				clearTimeout(timeout)
			}, 2000)
		}
	}, [isLoading])

	React.useEffect(() => {
		if (isLogin) {
			navigate('/auth/loading')
		}
	}, [isLogin])

	const onIdChange = React.useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const str = e.target.value
			const newAccount = produce(account, draft => {
				draft.email = str
			})

			setAccount(newAccount)
		},
		[account],
	)

	const onPwChange = React.useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const str = e.target.value
			const newAccount = produce(account, draft => {
				draft.password = str
			})

			setAccount(newAccount)
		},
		[account],
	)

	const onInputEnterSubmit = React.useCallback(
		(e: React.KeyboardEvent<HTMLInputElement>) => {
			if (e.key === 'Enter') {
				onLoginClick()
			}
		},
		[account],
	)

	const onLoginClick = React.useCallback(() => {
		if (account.email !== '' && account.password !== '') {
			reduxDispatch(
				authActions.LOGIN({ email: account.email, password: account.password }),
			)
		} else {
			reduxDispatch(authActions.LOGIN_ERROR_MESSAGE('로그인 정보를 다시 확인해주세요.'))
		}
	}, [account])

	const onForgotPasswordClick = React.useCallback(() => {
		setShowForgotPassword(!showForgotPassword)
	}, [showForgotPassword])

	const onRememberIdChange = () => {
		reduxDispatch(authActions.IS_REMEMBER_ID(!isRememberId))
	}

	return (
		<View css={style}>
			<SimpleNav />

			{showForgotPassword ? (
				<ForgotPasswordMessage onBackClick={onForgotPasswordClick} />
			) : (
				<View className={`loginPageInnerView ${isMobile ? 'mobile' : ''}`}>
					{isMobile && (
						<Logo className="logo" logoColor={grey[400]} textColor={grey[400]} />
					)}

					<View className="paper" css={showForgotPassword ? 'loginPageInnerView2' : null}>
						<View className="loginTitleView">
							<View className="loginTitleInnerView">
								<Image src={colorLogo} className="logoImage" />

								<Text className="loginTitle" category="h3">
									{systemName}
								</Text>
								<Text className="loginSubtitle" category="c2">
									{systemSubtitle}
								</Text>
							</View>
						</View>

						<View className="inputView">
							<TextField
								type="text"
								label="이메일"
								size="small"
								defaultValue={account.email}
								onChange={onIdChange}
								onKeyUp={onInputEnterSubmit}
							/>
						</View>

						<View className="inputView">
							<TextField
								type="password"
								label="패스워드"
								size="small"
								onChange={onPwChange}
								onKeyUp={onInputEnterSubmit}
							/>
						</View>

						<Divider />

						<View className="loginButtonDiv">
							{loginErrorMessage !== '' ? (
								<ErrorText className="errorMsg">{loginErrorMessage}</ErrorText>
							) : null}

							<View className="idSaveView">
								<Switch
									checked={isRememberId}
									onChange={onRememberIdChange}
									inputProps={{ 'aria-label': 'controlled' }}
								/>
								<Text category="p1" hint="500">
									아이디 저장
								</Text>
							</View>

							<Button onClick={onLoginClick} className="loginButton" variant="contained">
								로그인
							</Button>

							<Pressable onClick={onForgotPasswordClick}>
								<Text category="p2" hint="500" underline={true}>
									패스워드를 잊어버리셨나요?
								</Text>
							</Pressable>
						</View>
					</View>

					<View className="sloganOuter">
						<View className="sloganView">
							<View className="sloganMainView">
								<Text category="h1" className="sloganMain firstSloganMain">
									Be Strong
								</Text>
								<Text category="h1" className="sloganMain firstSloganMain">
									Be Brave
								</Text>
								<Text category="h1" className="sloganMain firstSloganMain">
									Be Fearless
								</Text>
								<Text category="h1" className="sloganMain">
									You are NEVER alone.
								</Text>
							</View>
							<Text category="h2" className="sloganSub">
								Joshua 1:9
							</Text>
						</View>
						<Image src={loginBg} className="sloganBackground" />
					</View>
				</View>
			)}

			<View className="versionView">
				<Text category="c1" hint="500">{`v ${packageJson.version}`}</Text>
			</View>
		</View>
	)
}

export default LoginPage

const style = css({
	position: 'relative',
	width: '100%',
	height: '100vh',

	'.versionView': {
		position: 'absolute',
		right: '2rem',
		bottom: '1rem',
	},

	'.logo': {
		position: 'absolute',
		zIndex: 9,
		top: '1rem',
		left: '1rem',
	},

	'.loginPageInnerView': {
		position: 'relative',
		display: 'block',
		width: '100%',
		height: '100%',
		margin: 'auto',
		overflowY: 'auto',
		overflowX: 'hidden',

		'.paper': {
			position: 'absolute',
			zIndex: 5,
			width: '20rem',
			height: 'fit-content',
			top: 0,
			bottom: 0,
			right: '12vw',
			margin: 'auto',
			padding: '2rem',
			background: '#FFF',
			borderRadius: '10px',

			'.loginTitleView': {
				position: 'relative',
				display: 'flex',
				justifyContent: 'center',
				width: '100%',
				height: '100%',
				marginBottom: '2rem',
				paddingTop: '2rem',
				textAlign: 'center',

				'.loginTitleInnerView': {
					flexShrink: 0,

					'.logoImage': {
						width: '7rem',
						marginTop: '0rem',
					},

					'.loginTitle': {
						width: '28rem',
						marginTop: '0.8rem',
						marginBottom: 0,
						top: 0,
						lineHeight: '10px',
						fontFamily: 'audiowideRegular',
						color: grey[900],
					},

					'.loginSubtitle': {
						fontSize: '17px',
						lineHeight: '10px',
						fontWeight: 300,
						letterSpacing: 0.2,
					},
				},
			},

			'.inputView': {
				width: '100%',
				marginBottom: '1.2rem',
			},

			'.input': {
				width: '100%',
			},

			'.loginButtonDiv': {
				position: 'relative',
				width: '100%',
				marginBottom: '0.8rem',
				paddingTop: 0,

				'.idSaveView': {
					display: 'flex',
					alignItems: 'center',
				},

				'.loginButton': {
					width: '100%',
					height: '2.6rem',
				},
			},
		},

		'.sloganOuter': {
			position: 'absolute',
			zIndex: 3,
			display: 'flex',
			justifyContent: 'flex-end',
			height: '100%',
			width: '100%',
			top: 0,
			left: 0,
			margin: 'auto',

			'.sloganView': {
				position: 'absolute',
				height: '30rem',
				width: 'fit-content',
				top: 0,
				bottom: 0,
				left: '4rem',
				margin: 'auto',
				textAlign: 'left',
				color: '#FFF',

				'.sloganMain': {
					margin: '0.7rem 0 ',
					fontSize: '70px',
				},
				'.sloganSub': {},
			},

			'.sloganBackground': {
				height: '100%',
				minHeight: '40rem',
				width: '100%',
			},
		},

		'&.mobile': {
			'.logo': {
				display: 'none',
			},

			'.paper': {
				height: 'fit-content',
				top: '3rem',
				bottom: 0,
				left: 0,
				right: 0,
				margin: 'auto',
			},

			'.sloganOuter': {
				position: 'absolute',
				top: 0,
				bottom: '44rem',
				left: 0,
				right: 0,
				margin: 'auto',

				'.sloganView': {
					marginLeft: 0,

					'.sloganMainView': {
						display: 'flex',

						'.sloganMain': {
							fontSize: '30px',

							'&.firstSloganMain': {
								marginRight: '0.5rem',
							},
						},
					},
					'.sloganSub': {
						width: '100%',
						marginTop: '0rem',
						fontSize: '16px',
						textAlign: 'center',
					},
				},
			},

			'@media (max-width: 500px)': {
				'.logo': {
					display: 'block',
				},

				'.paper': {
					position: 'relative',
					width: '100%',
					height: '100vh',
					top: 0,
					padding: '4rem',
					paddingTop: '5rem',
					background: '#fff',

					'.loginTitleView': {
						height: 'fit-content',
					},

					'.termsView': {
						marginTop: '2rem',
					},
				},

				'.sloganOuter': {
					display: 'none',
				},

				'.loginBackground': {
					display: 'none',
				},
			},

			'@media (max-height: 830px)': {
				'.paper': {
					top: 0,
				},
				'.sloganOuter': {
					display: 'none',
				},
			},
		},

		'@media (max-width: 1360px)': {
			'.paper': {
				right: '6rem',
			},

			'.sloganOuter': {
				width: '42vw',

				'.sloganView': {
					'.sloganMain': {
						fontSize: '50px',
					},
				},
			},
		},

		'@media (max-width: 1080px)': {
			'.paper': {
				right: '2rem',
			},

			'.sloganOuter': {
				justifyContent: 'flex-start',
				width: 'fit-content',

				'.sloganView': {
					marginLeft: '2rem',
					'.sloganMain': {
						fontSize: '40px',
					},
					'.sloganSub': {
						fontSize: '20px',
					},
				},
			},
		},

		'@media (max-width: 810px)': {
			'.paper': {
				top: '8rem',
				left: 0,
				right: 0,
				margin: 'auto',
			},

			'.sloganOuter': {
				position: 'relative',
				display: 'block',
				marginTop: '2rem',

				'.sloganView': {
					marginLeft: 0,

					'.sloganMainView': {
						display: 'flex',

						'.sloganMain': {
							fontSize: '30px',

							'&.firstSloganMain': {
								marginRight: '0.5rem',
							},
						},
					},
					'.sloganSub': {
						width: '100%',
						marginTop: '0rem',
						fontSize: '16px',
						textAlign: 'center',
					},
				},
			},
		},

		'@media (max-width: 500px)': {
			'.sloganOuter': {
				'.sloganView': {
					'.sloganMainView': {
						display: 'block',

						'.sloganMain': {
							margin: 0,
							fontSize: '24px',
						},
					},
					'.sloganSub': {
						width: '100%',
						marginTop: '0.5rem',
						fontSize: '14px',
						textAlign: 'center',
					},
				},
			},
		},

		'@media (max-height: 830px) and (max-width: 500px)': {
			'.paper': {
				height: 'fit-content',
				top: 0,
				bottom: 0,

				'.loginTitleView': {
					height: 'fit-content',
				},
			},
			'.sloganOuter': {
				display: 'none',
			},
		},
	},
})
